// hooks/useSession.ts
import { useCallback, useEffect, useState, useRef } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { AccountInfo, AuthError, InteractionStatus, EventType } from '@azure/msal-browser';
import { loginRequest } from 'authConfig';
import { appInsights, setTelemetryUserRole } from '../appInsights';
import useAuthRoles from './useAuthRoles';

export default function useSession() {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [isBusy, setIsBusy] = useState<boolean | null>(null);
  const {
    accessToken: initialAccessToken,
    isAdmin,
    isSuperAdmin,
    isManager,
    isUser,
    isOattRateOwner,
    isOattBillPreparer,
    isOattBillApprover,
    loading,
  } = useAuthRoles();
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [accessToken, setAccessToken] = useState<string | null>(initialAccessToken);
  const tokenRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    setAccessToken(initialAccessToken);
  }, [initialAccessToken]);

  useEffect(() => {
    const busy =
      inProgress === InteractionStatus.HandleRedirect ||
      inProgress === InteractionStatus.SsoSilent ||
      inProgress === InteractionStatus.Startup;
    // eslint-disable-next-line no-console
    console.log('[AUTH-DEBUG] useSession: inProgress =>', inProgress, '| isBusy =>', busy);
    setIsBusy(busy);
  }, [inProgress]);

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[AUTH-DEBUG] useSession: accounts changed, count =>', accounts.length, '| isLoggedIn will be =>', accounts.length > 0);
    setIsLoggedIn(accounts.length > 0);
  }, [accounts]);

  useEffect(() => {
    if (!isInitializedRef.current && accounts.length > 0) {
      const activeAccount = instance.getActiveAccount();
      if (!activeAccount) {
        instance.setActiveAccount(accounts[0]);
      }
      isInitializedRef.current = true;
    }
  }, [accounts, instance]);

  useEffect(() => {
    const cb = instance.addEventCallback((event) => {
      // Forwarded to App Insights as custom events (LoginSuccess / LoginFailure) so a
      // true login success *rate* is queryable — the exceptions table alone only ever
      // surfaces failures, with no denominator of total attempts.
      switch (event.eventType) {
        case EventType.LOGIN_SUCCESS: {
          const acc = (event.payload as any)?.account as AccountInfo | undefined;
          if (acc) {
            instance.setActiveAccount(acc);
          }
          // localAccountId (AAD object id) rather than username/email — ties telemetry to a
          // stable identity for Usage > Users/Cohorts without writing PII into App Insights.
          if (acc?.localAccountId) {
            appInsights?.setAuthenticatedUserContext(acc.localAccountId, undefined, true);
          }
          appInsights?.trackEvent({
            name: 'LoginSuccess',
            properties: { userId: acc?.localAccountId ?? '' },
          });
          break;
        }
        case EventType.LOGIN_FAILURE: {
          const errorCode = event.error instanceof AuthError ? event.error.errorCode : 'unknown';
          appInsights?.trackEvent({ name: 'LoginFailure', properties: { error: errorCode } });
          break;
        }
      }
    });
    return () => {
      if (cb) instance.removeEventCallback(cb);
    };
  }, [instance]);

  const refreshToken = useCallback(async () => {
    if (!account) return null;
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
        forceRefresh: true,
      });
      const newToken = response.accessToken;
      setAccessToken(newToken);
      return newToken;
    } catch (error) {
      if (error && typeof error === 'object' && 'errorCode' in error) {
        const msalError = error as any;
        if (
          msalError.errorCode === 'consent_required' ||
          msalError.errorCode === 'interaction_required' ||
          msalError.errorCode === 'login_required'
        ) {
          handleLogout();
        }
      }
      return null;
    }
  }, [account, instance]);

  useEffect(() => {
    if (!isLoggedIn || !account) {
      if (tokenRefreshTimerRef.current) {
        clearTimeout(tokenRefreshTimerRef.current);
        tokenRefreshTimerRef.current = null;
      }
      return;
    }

    const scheduleTokenRefresh = async () => {
      try {
        const response = await instance.acquireTokenSilent({ ...loginRequest, account });
        const expiresOn = response.expiresOn?.getTime();
        if (!expiresOn) return;

        const now = Date.now();
        const bufferTime = 5 * 60 * 1000;
        const refreshAt = expiresOn - bufferTime;
        const timeUntilRefresh = refreshAt - now;

        if (timeUntilRefresh > 0) {
          if (tokenRefreshTimerRef.current) clearTimeout(tokenRefreshTimerRef.current);
          tokenRefreshTimerRef.current = setTimeout(async () => {
            await refreshToken();
            scheduleTokenRefresh();
          }, timeUntilRefresh);
        } else {
          await refreshToken();
          scheduleTokenRefresh();
        }
      } catch (error: any) {
        // eslint-disable-next-line no-console
        console.error('Error scheduling token refresh:', error);
      }
    };

    scheduleTokenRefresh();

    return () => {
      if (tokenRefreshTimerRef.current) clearTimeout(tokenRefreshTimerRef.current);
    };
  }, [isLoggedIn, account, instance, refreshToken]);

  const handleLogin = useCallback(async () => {
    try {
      await instance.loginPopup(loginRequest);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Login failed:', error);
    }
  }, [instance]);

  const handleLogout = useCallback(async () => {
    if (tokenRefreshTimerRef.current) {
      clearTimeout(tokenRefreshTimerRef.current);
      tokenRefreshTimerRef.current = null;
    }
    // Fired before clearAuthenticatedUserContext() below so this event is still tied to the
    // signed-in identity — the other end of the session lifecycle from LoginSuccess/Failure.
    appInsights?.trackEvent({
      name: 'Logout',
      properties: { userId: account?.localAccountId ?? '' },
    });
    setAccessToken(null);
    setIsLoggedIn(false);
    isInitializedRef.current = false;
    appInsights?.clearAuthenticatedUserContext();
    setTelemetryUserRole([]);
    sessionStorage.clear();
    localStorage.clear();
    globalThis.location.href = globalThis.location.origin + '/login';
  }, [account, accounts, instance]);

  return {
    isLoggedIn,
    isAdmin,
    isSuperAdmin,
    isManager,
    isUser,
    isOattRateOwner,
    isOattBillPreparer,
    isOattBillApprover,
    loading,
    isBusy,
    accessToken,
    account,
    handleLogin,
    handleLogout,
    refreshToken,
  };
}